import {
  Checkbox,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuProps as MuiMenuProps,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { FC, memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import SelectArrow from '@/assets/svg/ui-elements/arrow-bottom.svg';
import SearchIcon from '@/assets/svg/ui-elements/search-lg.svg';

import { CustomTextField } from '../custom-text-field';
import { CustomMenuItem, SelectStyled } from './styled';

export type TOptions = {
  value: string;
  label: string;
  icon?: ReactNode;
};

interface CustomSelectProps {
  multiple?: boolean;
  value: string | string[];
  handleChange: (_e: SelectChangeEvent<unknown>) => void;
  options: readonly TOptions[];
  checked?: boolean;
  withSearch?: boolean;
  errorMessage?: string;
  error?: boolean;
  changed?: boolean;
  changedColor?: string;
  fullWidth?: boolean;
  displayEmpty?: boolean;
  placeholder?: string;
  renderValue?: (_value: unknown) => React.ReactNode;
  /**
   * Accessible name for the combobox. Required when the select has no visible
   * `<label>` — a `renderValue` placeholder is not an accessible name.
   */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  bigLabel?: boolean;
  labelColor?: string;
  isFullWidthFormControl?: boolean;
  MenuProps?: Partial<MuiMenuProps>;
}

export const CustomSelect: FC<CustomSelectProps> = memo(
  ({
    multiple = false,
    value,
    handleChange,
    options,
    checked,
    withSearch,
    error = false,
    errorMessage = '',
    disabled = false,
    changed = false,
    changedColor,
    isFullWidthFormControl = true,
    MenuProps,
    ariaLabel,
    ...props
  }) => {
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const resetSearch = () => setSearch('');
    const filteredOptions = options.filter((o) =>
      String(o.label ?? '')
        .toLowerCase()
        .includes(search.toLowerCase()),
    );

    const renderSelected = useCallback(
      (value: unknown): ReactNode => {
        const arr = Array.isArray(value) ? (value as unknown[]) : [value];
        const labels = arr.map((v) => {
          const s = String(v ?? '');
          return options.find((o) => o.value === s)?.label ?? s;
        });

        const MAX = 3;
        if (labels.length <= MAX) return labels.join(', ');
        return `${labels.slice(0, MAX).join(', ')} … +${labels.length - MAX}`;
      },
      [options],
    );

    const standardMenuProps = {
      MenuListProps: { disablePadding: true },
      PopoverProps: { disableScrollLock: false },
      autoFocus: false,
      slotProps: {
        paper: {
          sx: {
            mt: '10px',
            borderRadius: '12px',
            background: '#171B2B !important',
            maxHeight: 400,
            overflowY: 'auto',
            p: withSearch ? '0px 8px 8px' : '8px',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.main,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.primary.main} transparent`,
          },
        },
      },
    };

    const isMulti = Boolean(multiple);
    const selectedValues = isMulti && Array.isArray(value) ? (value as string[]) : [];

    const allOptionValues = useMemo(() => options.map((o) => o.value), [options]);

    const emitMultiChange = (next: string[]) => {
      handleChange({
        target: { value: next },
      } as SelectChangeEvent<unknown>);
    };

    const isAllSelected =
      allOptionValues.length > 0 && allOptionValues.every((v) => selectedValues.includes(v));
    const isNoneSelected = selectedValues.length === 0;

    const handleClearClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!isMulti || isNoneSelected) return;

      emitMultiChange([]);
    };

    const handleSelectAllClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!isMulti || isAllSelected) return;
      if (!allOptionValues.length) return;

      emitMultiChange(allOptionValues);
    };

    return (
      <FormControl
        fullWidth={isFullWidthFormControl}
        error={!!error}
        variant="outlined"
        sx={{ position: 'relative' }}
      >
        <SelectStyled
          error={!!error}
          changed={changed}
          changedColor={changedColor}
          multiple={isMulti}
          value={value}
          onChange={handleChange}
          onOpen={() => {
            resetSearch();
            setTimeout(() => searchInputRef.current?.focus(), 0);
          }}
          onClose={resetSearch}
          IconComponent={SelectArrow}
          MenuProps={MenuProps || standardMenuProps}
          renderValue={props.renderValue ?? renderSelected}
          // MUI only forwards `aria-label` onto the `role="combobox"` element
          // when it comes through `inputProps`; a bare prop lands on the
          // wrapper and leaves the combobox unnamed.
          inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? theme.palette.error.main : undefined,
            },
          }}
          disabled={disabled}
          {...props}
        >
          {multiple && checked && (
            <ListSubheader
              disableSticky
              sx={{
                px: 2,
                py: 1.5,
                background: '#171B2B',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body2"
                  aria-disabled={isAllSelected}
                  sx={{
                    color: isAllSelected ? '#FFFFFF61' : '#39CA88',
                    cursor: isAllSelected ? 'default' : 'pointer',
                    pointerEvents: isAllSelected ? 'none' : 'auto',
                    userSelect: 'none',
                  }}
                  onClick={handleSelectAllClick}
                >
                  Select all
                </Typography>
                <Typography
                  variant="body2"
                  aria-disabled={isNoneSelected}
                  sx={{
                    color: isNoneSelected ? '#FFFFFF61' : '#39CA88',
                    cursor: isNoneSelected ? 'default' : 'pointer',
                    pointerEvents: isNoneSelected ? 'none' : 'auto',
                    userSelect: 'none',
                  }}
                  onClick={handleClearClick}
                >
                  Clear
                </Typography>
              </Stack>
            </ListSubheader>
          )}

          {withSearch && (
            <ListSubheader
              onKeyDown={(e) => e.stopPropagation()}
              disableSticky={false}
              sx={{
                position: 'sticky',
                top: 0,
                p: 1,
                zIndex: 1,
                background: '#171B2B',
              }}
            >
              <CustomTextField
                placeholder="Search"
                value={search}
                noLabel
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    inputRef: searchInputRef,
                    sx: {
                      width: '100%',
                      p: '6px !important',
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    onKeyDown: (e) => e.stopPropagation(),
                  },
                }}
              />
            </ListSubheader>
          )}

          {filteredOptions.map((o) => {
            return (
              <CustomMenuItem key={o.value} value={o.value}>
                <Stack width="100%" alignItems="center" gap="8px" justifyContent="space-between">
                  <Stack alignItems="center" spacing={1} gap="8px">
                    {o.icon && o.icon}
                    <Typography
                      fontWeight={400}
                      sx={{
                        marginTop: '0 !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {o.label}
                    </Typography>
                  </Stack>
                  {checked && (
                    <Checkbox
                      checked={Array.isArray(value) && value.includes(o.value)}
                      sx={{ padding: '0px !important' }}
                    />
                  )}
                </Stack>
              </CustomMenuItem>
            );
          })}
        </SelectStyled>

        {error && (
          <Typography
            variant="body2"
            color="error"
            sx={{ position: 'absolute', bottom: '-20px', left: 0 }}
          >
            {errorMessage}
          </Typography>
        )}
      </FormControl>
    );
  },
);
